import React, { useContext, useMemo, useState } from "react";
import { useAccountantView } from "@/contexts/AccountantViewContext";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  CheckCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  ArrowUturnLeftIcon,
  DocumentDuplicateIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import { InputNumber } from "@/components/ui/input";
import { Tooltip } from "@/components/ui/tooltip";
import { Select } from "@/components/ui/select";
import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import {
  dayjs,
  fiatCurrencyCodes,
  formatAmount,
  PAGINATED_API,
  useGetAssets,
  usePaginatedSearch,
  useTxnUserValue,
  getCurrencySymbol,
  useProfile,
  getBlockExplorerTxUrl,
  getBlockExplorerName,
  hasBlockExplorer,
  copyToClipboard,
  toast,
} from "@moonscape/shared";
import { useQueryClient } from "@tanstack/react-query";
import { TransactionListContext } from "@/contexts";
import clsx from "clsx";
import { CoinIcon, JSONViewer } from "@/components";
import { SpamBadge } from "@/components/SpamBadge";
import { CodeBracketIcon } from "@heroicons/react/24/outline";
import { useCostBasis } from "@/hooks/useCostBasis";
import { usePipelineState } from "@/hooks/usePipelineState";
import { CostBasisForm } from "@/components/Tax/CostBasisForm";
import { AuditErrorAlert } from "@/components/audit/AuditErrorAlert";
import { Callout, DHead, Fig, Foot, Lab, Pill, Tag } from "@/components/ms";

/** The mock's one-line verdict: "Disposal · taxable". */
const getVerdict = (type: string) => {
  const lowerType = type?.toLowerCase();
  if (["sell", "gift_sent", "donation", "loss_writeoff"].includes(lowerType))
    return "Disposal · taxable";
  if (["trade", "swap", "convert"].includes(lowerType)) return "Disposal · taxable";
  if (
    [
      "airdrop",
      "mining",
      "staking",
      "staking reward",
      "interest",
      "dividend",
      "income",
      "rewards",
      "earn_payout",
    ].includes(lowerType)
  )
    return "Income · taxable";
  if (["buy", "gift_received"].includes(lowerType)) return "Acquisition · not taxable";
  return "Transfer · not taxable";
};

/** The short headline over the summary sentence: "Sold 0.65 BTC". */
const getHeadline = (transaction: any) => {
  const type = transaction.type?.toLowerCase();
  const verbs: Record<string, string> = {
    buy: "Bought",
    sell: "Sold",
    deposit: "Deposited",
    withdrawal: "Withdrew",
    send: "Sent",
    receive: "Received",
    trade: "Traded",
    swap: "Swapped",
    convert: "Converted",
    gift_sent: "Gifted",
    gift_received: "Received as gift",
    donation: "Donated",
    loss_writeoff: "Wrote off",
  };
  const verb = verbs[type] || "Received";
  const amount = transaction.getDisplayAmount?.() ?? "";
  return amount && amount !== "-" ? `${verb} ${amount}` : verb;
};

// Generate readable summary sentence
const generateSummary = (transaction: any, baseCurrency: string) => {
  const type = transaction.type?.toLowerCase();
  const currencySymbol = getCurrencySymbol(baseCurrency);

  if (type === 'buy') {
    return `You bought ${formatAmount(transaction.buyqty)} ${transaction.buyasset} on ${transaction.exchange} for ${formatAmount(transaction.sellqty)} ${transaction.sellasset}${transaction.final_value ? ` (${currencySymbol}${formatAmount(transaction.final_value)} ${transaction.final_valueasset || baseCurrency})` : ''}`;
  }

  if (type === 'sell') {
    return `You sold ${formatAmount(transaction.sellqty)} ${transaction.sellasset} on ${transaction.exchange} for ${formatAmount(transaction.buyqty)} ${transaction.buyasset}${transaction.final_value ? ` (${currencySymbol}${formatAmount(transaction.final_value)} ${transaction.final_valueasset || baseCurrency})` : ''}`;
  }

  if (type === 'deposit') {
    return `You deposited ${formatAmount(transaction.buyqty)} ${transaction.buyasset} to ${transaction.exchange}`;
  }

  if (type === 'withdrawal') {
    return `You withdrew ${formatAmount(transaction.sellqty)} ${transaction.sellasset} from ${transaction.exchange}`;
  }

  if (type === 'swap' || type === 'convert' || type === 'trade') {
    return `You swapped ${formatAmount(transaction.sellqty)} ${transaction.sellasset} for ${formatAmount(transaction.buyqty)} ${transaction.buyasset} on ${transaction.exchange}`;
  }

  if (type === 'receive') {
    return `You received ${formatAmount(transaction.buyqty)} ${transaction.buyasset} on ${transaction.exchange}`;
  }

  if (type === 'send') {
    return `You sent ${formatAmount(transaction.sellqty)} ${transaction.sellasset} from ${transaction.exchange}`;
  }

  // Income types
  const incomeTypes = ['airdrop', 'mining', 'staking', 'staking reward', 'interest', 'dividend', 'income', 'rewards', 'earn_payout'];
  if (incomeTypes.includes(type)) {
    const displayType = type === 'earn_payout' ? 'earn payout' : type;
    return `You received ${formatAmount(transaction.buyqty)} ${transaction.buyasset} from ${displayType} on ${transaction.exchange}`;
  }

  if (type === 'gift_sent') {
    return `You gifted ${formatAmount(transaction.sellqty)} ${transaction.sellasset} from ${transaction.exchange}${transaction.final_value ? ` (${currencySymbol}${formatAmount(transaction.final_value)} ${transaction.final_valueasset || baseCurrency})` : ''}`;
  }

  if (type === 'gift_received') {
    return `You received ${formatAmount(transaction.buyqty)} ${transaction.buyasset} as a gift on ${transaction.exchange}${transaction.final_value ? ` (${currencySymbol}${formatAmount(transaction.final_value)} ${transaction.final_valueasset || baseCurrency})` : ''}`;
  }

  if (type === 'donation') {
    return `You donated ${formatAmount(transaction.sellqty)} ${transaction.sellasset} from ${transaction.exchange}${transaction.final_value ? ` (${currencySymbol}${formatAmount(transaction.final_value)} ${transaction.final_valueasset || baseCurrency})` : ''}`;
  }

  // Loss write-off
  if (type === 'loss_writeoff') {
    return `Loss Write-off - Proceeds: ${currencySymbol}0. You wrote off ${formatAmount(transaction.sellqty)} ${transaction.sellasset} as a total loss on ${transaction.exchange}${transaction.final_value ? ` (Cost basis: ${currencySymbol}${formatAmount(Math.abs(transaction.final_value))} ${transaction.final_valueasset || baseCurrency})` : ''}`;
  }

  return `${type?.charAt(0).toUpperCase()}${type?.slice(1)} transaction on ${transaction.exchange}`;
};

export const TransactionDetail = ({ transaction, page, onClose }) => {
  const { isViewingClient } = useAccountantView();
  const { isEditing, setIsEditing } = useContext(TransactionListContext);
  const { busy: pipelineBusy } = usePipelineState();
  const queryClient = useQueryClient();
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [mutationSuccess, setMutationSuccess] = useState(false);
  const mutation = useTxnUserValue();
  const { profile } = useProfile();

  // Cost basis state
  const [showCostBasisForm, setShowCostBasisForm] = useState(false);
  const {
    costBasisData,
    isLoading: isCostBasisLoading,
    setCostBasis,
    revertCostBasis,
    isUpdating: isCostBasisUpdating,
    setError: costBasisSetError,
    revertError: costBasisRevertError,
  } = useCostBasis(transaction?.txn_id || null);

  // Handler to copy transaction ID to clipboard
  const handleCopyTxId = async () => {
    if (!transaction.tradeid) return;

    const success = await copyToClipboard(transaction.tradeid);
    if (success) {
      toast.success('Transaction ID copied to clipboard');
    } else {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Handler to open transaction in block explorer
  const handleOpenBlockExplorer = () => {
    if (!transaction.tradeid || !transaction.exchange) return;

    const url = getBlockExplorerTxUrl(transaction.exchange, transaction.tradeid);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('Block explorer not available for this chain');
    }
  };

  // Handler to submit cost basis
  const handleCostBasisSubmit = (costBasis: number, note?: string) => {
    if (!transaction?.txn_id) {
      toast.error('Transaction ID not available');
      return;
    }

    setCostBasis({
      txn_id: transaction.txn_id,
      cost_basis: costBasis,
      note,
    });

    // Hide form on success
    setShowCostBasisForm(false);
    toast.success('Cost basis updated successfully');
  };

  // Handler to revert cost basis
  const handleCostBasisRevert = () => {
    if (!transaction?.txn_id) {
      toast.error('Transaction ID not available');
      return;
    }

    if (window.confirm('Are you sure you want to revert to the calculated cost basis?')) {
      revertCostBasis(transaction.txn_id);
      toast.success('Reverted to calculated cost basis');
    }
  };

  const {
    data: exchanges,
    searchQuery,
    setSearchQuery,
    hasMore,
    fetchNextPage,
    isLoading: isQueryLoadingExchanges,
    isFetchingNextPage,
    resetSearch,
  } = usePaginatedSearch(PAGINATED_API.getExchanges);

  const {
    assets,
    setSearchQuery: setSearchQueryAssets,
    isQueryLoading: isQueryLoadingAssets,
  } = useGetAssets({ initialSearchQuery: "USD" });

  const summary = generateSummary(transaction, profile?.baseCurrency || 'USD');
  const headline = getHeadline(transaction);
  const verdict = getVerdict(transaction.type);

  const rowValues = [
    {
      label: "Type",
      key: "type",
      value: transaction.type,
      inputType: "select",
      options: [
        // Trade types
        "trade",
        "buy",
        "sell",
        "convert",
        "swap",
        "loss_writeoff",
        // Transfer types
        "deposit",
        "withdrawal",
        "send",
        "receive",
        // Gift / Donation types
        "gift_sent",
        "gift_received",
        "donation",
        // Income types (for reclassifying receives as taxable income)
        "income",
        "airdrop",
        "mining",
        "staking",
        "staking reward",
        "interest",
        "dividend",
        "rewards",
        "earn_payout",
      ],
      icon: transaction.type ? null : (
        <Tooltip title="The type of transaction (e.g., trade, deposit, withdrawal, etc.)">
          <ExclamationTriangleIcon className="h-4 w-4 text-ms-tan" />
        </Tooltip>
      ),
    },
    {
      label: "Exchange",
      key: "exchange",
      value: transaction.exchange,
      inputType: "select",
      options: exchanges.map((ex) => ex.id),
      onSearch: setSearchQuery,
      loading: isQueryLoadingExchanges,
      postProcess: (values) => {
        return { exchange_id: values.exchange };
      },
      icon: transaction.exchange ? null : (
        <Tooltip title="The exchange where the transaction took place">
          <ExclamationTriangleIcon className="h-4 w-4 text-ms-tan" />
        </Tooltip>
      ),
    },
    {
      label: transaction.isDeposit() ? "Amount" : "Bought",
      key: "buyqty",
      subKey: "buyasset",
      value: formatAmount(transaction.buyqty),
      subKeyValue: transaction.buyasset,
      inputType: "number+select",
      options: assets?.map((as) => as?.symbol),
      onSearch: setSearchQueryAssets,
      loading: isQueryLoadingAssets,
      postProcess: (values) => {
        let acc = {};
        if (values.buyqty) acc["buy_qty"] = values.buyqty;
        if (values.buyasset) acc["buy_asset"] = values.buyasset;
        return acc;
      },
    },
    {
      label: transaction.isWithdrawal() ? "Amount" : "Paid",
      key: "sellqty",
      subKey: "sellasset",
      value: formatAmount(transaction.sellqty),
      subKeyValue: transaction.sellasset,
      inputType: "number+select",
      options: assets?.map((as) => as?.symbol),
      onSearch: setSearchQueryAssets,
      loading: isQueryLoadingAssets,
      postProcess: (values) => {
        let acc = {};
        if (values.sellqty) acc["sell_qty"] = values.sellqty;
        if (values.sellasset) acc["sell_asset"] = values.sellasset;
        return acc;
      },
    },
    {
      label: "Fees",
      key: "feeqty",
      subKey: "feeasset",
      value: formatAmount(transaction.feeqty),
      subKeyValue: transaction.feeasset,
      inputType: "number+select",
      options: assets?.map((as) => as?.symbol),
      onSearch: setSearchQueryAssets,
      loading: isQueryLoadingAssets,
      tone: "tan",
      postProcess: (values) => {
        let acc = {};
        if (values.feeqty) acc["fee_qty"] = values.feeqty;
        if (values.feeasset) acc["fee_asset"] = values.feeasset;
        return acc;
      },
    },
    {
      label: `Total value (${transaction.final_valueasset?.toUpperCase() || profile?.baseCurrency?.toUpperCase() || 'USD'})`,
      inputType: "number+select",
      key: "final_value",
      value: formatAmount(transaction.final_value),
      subKey: "final_valueasset",
      subKeyValue: transaction.final_valueasset,
      total: true,
      icon: transaction.missing_final_value ? (
        transaction.final_feevalue !== 0 ? (
          <Tooltip title="Fiat value is missing or incomplete"><ExclamationTriangleIcon className="h-4 w-4 text-ms-tan" /></Tooltip>
        ) : (
          <Tooltip title="Fiat value calculated automatically"><InformationCircleIcon className="h-4 w-4 text-ms-mute" /></Tooltip>
        )
      ) : (
        <Tooltip title="Fiat value verified"><CheckCircleIcon className="h-4 w-4 text-ms-green" /></Tooltip>
      ),
      options: fiatCurrencyCodes,
      postProcess: (values) => {
        let acc = {};
        if (values.final_value) acc["value"] = values.final_value;
        if (values.final_valueasset)
          acc["valueasset"] = values.final_valueasset;
        return acc;
      },
    },
    {
      label: `Fee value (${transaction.final_valueasset?.toUpperCase() || profile?.baseCurrency?.toUpperCase() || 'USD'})`,
      inputType: "number+select",
      key: "final_feevalue",
      value: formatAmount(transaction.final_feevalue),
      subKey: "final_valueasset",
      subKeyValue: transaction.final_valueasset,
      options: fiatCurrencyCodes,
      postProcess: (values) => {
        let acc = {};
        if (values.final_feevalue) acc["feevalue"] = values.final_feevalue;
        return acc;
      },
    },
    {
      label: "Date & Time",
      key: "date",
      value: transaction.formattedTimestamp(),
      inputType: "datetime",
      postProcess: (values) => {
        return { timestamp: values?.date?.valueOf() };
      },
      icon:
        transaction.formattedTimestamp() !== "-" ? null : (
          <Tooltip title="Date and time when the transaction occurred">
            <ExclamationTriangleIcon className="h-4 w-4 text-ms-tan" />
          </Tooltip>
        ),
    },
    {
      label: "Transaction ID",
      key: "exchangeId",
      value: transaction.tradeid,
      postProcess: (values) => {
        return { exchange_id: values?.exchangeId };
      },
      customRender: true, // Flag for custom rendering
    },
    {
      label: "Protocol",
      key: "protocol",
      value: transaction.protocol || "-",
      inputType: "readonly",
    },
    {
      label: "Category",
      key: "category",
      value: transaction.category ? transaction.formattedCategory() : "-",
      inputType: "readonly",
    },
    {
      label: "Note",
      key: "final_valuenote",
      inputType: "textarea",
      value: transaction.final_valuenote ? transaction.final_valuenote : "-",
      postProcess: (values) => {
        let acc = {};
        if (values.final_valuenote) acc["valuenote"] = values.final_valuenote;
        return acc;
      },
    },
  ];

  const initialValues = useMemo(() => {
    const obj = {};
    rowValues.forEach((row) => {
      obj[row.key] = transaction[row.key] ?? "";
      if (row.subKey) {
        obj[row.subKey] = transaction[row.subKey] ?? "";
      }
    });
    return obj;
  }, [transaction]);

  const validationSchema = Yup.object(
    rowValues.reduce((acc, row) => {
      acc[row.key] = Yup.mixed().nullable();
      return acc;
    }, {})
  );

  const handleSubmit = (values) => {
    const changedValues = Object.keys(initialValues).reduce((acc, key) => {
      if (values[key] !== initialValues[key]) {
        const row = rowValues.find((r) => r.key === key || r.subKey === key);
        acc = {
          ...acc,
          ...(row?.postProcess?.(values) || { [key]: values[key] }),
        };
      }

      return acc;
    }, {} as Record<string, any>);
    mutation.mutate({
      updatedValues: changedValues,
      extTxnId: transaction.ext_txn_id,
    } as any);
    setMutationSuccess(true);
  };

  const hasChanges = (values) => {
    return Object.keys(initialValues).some(
      (key) => values[key] !== initialValues[key]
    );
  };

  // Group rows by section
  const transactionRows = rowValues.filter(r => ['type', 'exchange', 'date', 'exchangeId', 'protocol', 'category'].includes(r.key));
  const tradeRows = rowValues.filter(r => ['buyqty', 'sellqty', 'feeqty', 'final_value', 'final_feevalue'].includes(r.key));
  const noteRows = rowValues.filter(r => r.key === 'final_valuenote');

  const renderFormRow = (row, values, setFieldValue, handleBlur) => {
    if (!row.value) return null;

    const readValue = row.key === 'exchangeId' ? (
      // Custom rendering for Transaction ID with copy and block explorer buttons
      <span className="flex items-center justify-start gap-1.5 ms700:justify-end">
        <span className="max-w-[200px] truncate font-mono text-[11.5px] text-ms-ink">
          {row.value || '-'}
        </span>
        {row.value && (
          <>
            <Tooltip title="Copy transaction ID">
              <button
                type="button"
                onClick={handleCopyTxId}
                className="p-1 text-ms-dim transition-colors hover:text-ms-ink"
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
              </button>
            </Tooltip>
            {hasBlockExplorer(transaction.exchange) && (
              <Tooltip title={`View on ${getBlockExplorerName(transaction.exchange)}`}>
                <button
                  type="button"
                  onClick={handleOpenBlockExplorer}
                  className="p-1 text-ms-dim transition-colors hover:text-ms-link"
                >
                  <LinkIcon className="h-4 w-4" />
                </button>
              </Tooltip>
            )}
          </>
        )}
      </span>
    ) : (
      <span
        className={clsx(
          "font-mono tabular-nums text-[13.5px]",
          row.tone === "tan" ? "text-ms-tan" : "text-ms-ink",
          row.total && "font-bold"
        )}
      >
        {`${row.value} ${row.subKeyValue ? row.subKeyValue : ""}`}
      </span>
    );

    return (
      <div
        key={row.key}
        className={clsx(
          "flex flex-col gap-2 border-t border-ms-line py-[11px] first:border-t-0 ms700:flex-row ms700:items-center ms700:gap-4",
          row.total && "border-b-2 border-b-ms-ink"
        )}
      >
        <div
          className={clsx(
            "flex min-w-[140px] flex-1 items-center gap-2 text-left text-[13.5px] tracking-[-0.01em] text-ms-mute",
            row.total && "font-bold text-ms-ink"
          )}
        >
          {row.label}
          {row.icon}
        </div>
        <div className="flex-1 text-left ms700:text-right">
          {isEditing ? (
            row.inputType === "select" ? (
              <Select
                className="w-full"
                value={values[row.key]}
                onChange={(val) => setFieldValue(row.key, val)}
                placeholder={`Select ${row.label}`}
                options={row.options?.map((opt) => ({
                  value: opt,
                  label: opt,
                })) || []}
              />
            ) : row.inputType === "datetime" ? (
              <input
                type="datetime-local"
                step="1"
                className="w-full border border-ms-line bg-transparent px-3 py-2 font-mono text-[12.5px] text-ms-ink outline-none focus:border-ms-line2"
                value={
                  (() => {
                    const val = values[row.key] || (row.value !== "-" ? dayjs(row.value) : dayjs());
                    return dayjs(val).format("YYYY-MM-DDTHH:mm:ss");
                  })()
                }
                onChange={(e) =>
                  setFieldValue(row.key, e.target.value ? dayjs(e.target.value) : null)
                }
              />
            ) : row.inputType === "number+select" ? (
              <div className="flex gap-2">
                <InputNumber
                  className="w-1/2"
                  placeholder="Enter value"
                  value={values[row.key]}
                  onChange={(val) => setFieldValue(row.key, val)}
                />
                <Select
                  className="w-1/2"
                  value={values[row.subKey]}
                  onChange={(val) =>
                    setFieldValue(row.subKey, val)
                  }
                  placeholder="Asset"
                  options={row.options?.map((opt) => ({
                    value: opt,
                    label: opt,
                  })) || []}
                />
              </div>
            ) : row.inputType === "number" ? (
              <InputNumber
                className="w-full"
                placeholder="Enter value"
                value={values[row.key]}
                onChange={(val) => setFieldValue(row.key, val)}
              />
            ) : row.inputType === "textarea" ? (
              <textarea
                rows={4}
                className="flex w-full border border-ms-line bg-transparent px-3 py-2 text-[13px] text-ms-ink outline-none placeholder:text-ms-dim focus:border-ms-line2"
                placeholder="Add a note (optional)"
                maxLength={255}
                defaultValue={
                  row.value === "-" ? undefined : row.value
                }
                onChange={(e) =>
                  setFieldValue(row.key, e.target.value)
                }
              />
            ) : (
              readValue
            )
          ) : (
            readValue
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="text-left">
      {mutationError && (
        <Callout tone="neg" className="mb-[var(--gap)]">
          <b>We couldn't save that.</b> {mutationError}
        </Callout>
      )}
      {mutationSuccess && (
        <Callout className="mb-[var(--gap)]">
          <b>Saved.</b> This transaction has been updated.
        </Callout>
      )}
      {transaction?.getMissingFiatValueProperties() && (
        <Callout tone="tan" className="mb-[var(--gap)]">
          {transaction.getMissingFiatValueProperties()}
        </Callout>
      )}
      {/* Audit Error Context */}
      {transaction?.error_msg && (
        <AuditErrorAlert
          error={transaction}
          className="mb-[var(--gap)]"
          onActionComplete={() => {
            // Invalidate queries to refresh the transaction data
            queryClient.invalidateQueries({
              queryKey: [`transaction/${transaction.ext_txn_id}`],
            });
            queryClient.invalidateQueries({
              queryKey: [`audit-${page}`],
            });
          }}
        />
      )}

      {/* The verdict hero */}
      <div className="mb-[var(--gap)]">
        <div className="flex items-center gap-3.5">
          <CoinIcon
            coinSymbol={transaction.getCoinSymbol?.()}
            iconUrl={transaction.getCoinIconUrl?.()}
            coingeckoId={transaction.getCoinGeckoId?.()}
            className="h-10 w-10 flex-none rounded-full"
          />
          <div className="min-w-0">
            <Lab className="block">{verdict}</Lab>
            <h1 className="mt-1.5 text-[24px] font-semibold tracking-[-0.03em] text-ms-ink">
              {headline}
            </h1>
          </div>
        </div>

        <div className="mt-3.5 flex flex-wrap items-center gap-[7px]">
          {transaction.exchange && (
            <Tag>{transaction.getFormattedExchangeName?.() || transaction.exchange}</Tag>
          )}
          {transaction.formattedTimestamp() !== "-" && (
            <Tag>{transaction.formattedTimestamp()}</Tag>
          )}
          {transaction.protocol && <Tag>{transaction.protocol}</Tag>}
          {transaction.category && <Tag>{transaction.formattedCategory()}</Tag>}
          {transaction.missing_final_value ? (
            pipelineBusy ? (
              <Tag tone="live">Still calculating</Tag>
            ) : (
              <Tag tone="warn">Unpriced</Tag>
            )
          ) : (
            <Tag tone="pos">Priced</Tag>
          )}
          {transaction.spam_category && (
            <SpamBadge
              category={transaction.spam_category}
              note={transaction.txn_note}
            />
          )}
        </div>

        <p className="mt-3.5 text-[14.5px] leading-[1.5] text-ms-ink">{summary}</p>

        {transaction.txn_note && !transaction.spam_category && (
          <p className="mt-2 text-[12px] text-ms-mute">{transaction.txn_note}</p>
        )}

        {transaction.type === 'donation' && (
          <Callout className="mt-3.5">
            This charitable donation may qualify for tax relief. In the US, donations of
            crypto held over 1 year may be deductible at fair market value (Schedule A).
            In the UK, donations to registered charities may be exempt from CGT. Consult
            your tax advisor.
          </Callout>
        )}
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          setFieldValue,
          resetForm,
        }) => (
          <Form>
            {/* Two-column layout for larger screens */}
            <div className="grid grid-cols-1 gap-[var(--gap)] ms900:grid-cols-2">
              {/* Transaction Summary */}
              <section>
                <DHead label="The transaction" right="As imported" />
                <div>
                  {transactionRows.map(row => renderFormRow(row, values, setFieldValue, handleBlur))}
                </div>
              </section>

              {/* Trade Details */}
              <section>
                <DHead label="Amounts" right="As disposed" />
                <div>
                  {tradeRows.map(row => renderFormRow(row, values, setFieldValue, handleBlur))}
                </div>
              </section>

              {/* Cost Basis Section */}
              <section>
                <DHead
                  label={transaction.type === 'gift_received' ? "Donor's cost basis" : "Manual cost basis"}
                  right={
                    costBasisData?.manual_cost_basis !== null && !showCostBasisForm
                      ? (isCostBasisUpdating ? "Reverting…" : "Revert to calculated")
                      : undefined
                  }
                  onClick={
                    costBasisData?.manual_cost_basis !== null && !showCostBasisForm
                      ? handleCostBasisRevert
                      : undefined
                  }
                />

                {/* Display current manual cost basis */}
                {costBasisData?.manual_cost_basis !== null && !showCostBasisForm ? (
                  <>
                    <Fig
                      label="Cost basis"
                      sub="Manual override active"
                      strong
                      value={`${getCurrencySymbol(profile?.baseCurrency || 'USD')}${formatAmount(costBasisData?.manual_cost_basis || 0)}`}
                    />
                    {costBasisData?.manual_cost_basis_set_at && (
                      <Fig
                        label="Set on"
                        tone="mute"
                        value={dayjs(costBasisData.manual_cost_basis_set_at).format('MMM D, YYYY [at] HH:mm')}
                      />
                    )}
                    {costBasisData?.cost_basis_note && (
                      <Fig label="Note" tone="mute" value={costBasisData?.cost_basis_note} />
                    )}
                    <div className="mt-3.5">
                      <Pill variant="ghost" onClick={() => setShowCostBasisForm(true)}>
                        <PencilIcon className="h-4 w-4" />
                        Edit
                      </Pill>
                    </div>
                  </>
                ) : showCostBasisForm ? (
                  <div className="pt-3.5">
                    <CostBasisForm
                      txnId={transaction?.txn_id || 0}
                      initialValue={costBasisData?.manual_cost_basis || undefined}
                      initialNote={costBasisData?.cost_basis_note || undefined}
                      onSubmit={handleCostBasisSubmit}
                      onCancel={() => setShowCostBasisForm(false)}
                      isLoading={isCostBasisUpdating}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-start gap-3.5 pt-3.5">
                    <p className="text-[12px] leading-[1.5] text-ms-mute">
                      {transaction.type === 'gift_received'
                        ? "Enter the donor's original cost basis (IRC §1015). If unknown, the system will use $0. Contact the donor for their original purchase price."
                        : "No manual cost basis has been set for this transaction. The system will use the automatically calculated value."}
                    </p>
                    <Pill variant="ghost" onClick={() => setShowCostBasisForm(true)}>
                      <PencilIcon className="h-4 w-4" />
                      {transaction.type === 'gift_received' ? "Set donor's cost basis" : "Set manual cost basis"}
                    </Pill>
                  </div>
                )}

                {/* Error messages */}
                {costBasisSetError && (
                  <Callout tone="neg" className="mt-3.5">
                    <b>Failed to update cost basis.</b> {String(costBasisSetError)}
                  </Callout>
                )}
                {costBasisRevertError && (
                  <Callout tone="neg" className="mt-3.5">
                    <b>Failed to revert cost basis.</b> {String(costBasisRevertError)}
                  </Callout>
                )}
              </section>

              {/* Notes */}
              <section>
                <DHead label="Notes" right="Yours" />
                <div>
                  {transaction.txn_note && (
                    <Fig label="Transaction note" tone="mute" value={transaction.txn_note} />
                  )}
                  {noteRows.map(row => renderFormRow(row, values, setFieldValue, handleBlur))}
                </div>
              </section>
            </div>

            {/* Provenance — raw exchange data */}
            {transaction.raw_exchange_txn_id && transaction.raw_data && (
              <Disclosure as="section" className="mt-[var(--gap)]">
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex w-full items-baseline justify-between gap-3 border-b border-ms-ink pb-[7px] text-left transition-colors hover:text-ms-ink">
                      <Lab className="flex items-center gap-2">
                        <CodeBracketIcon className="h-3.5 w-3.5" />
                        Provenance · raw exchange data
                      </Lab>
                      <ChevronDownIcon
                        className={clsx(
                          "h-4 w-4 flex-none text-ms-dim transition-transform",
                          open && "rotate-180"
                        )}
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel>
                      <Fig
                        label="Source record"
                        value={transaction.raw_source_txn_id || 'N/A'}
                      />
                      <Fig
                        label="Imported"
                        value={
                          transaction.raw_imported_at
                            ? dayjs(transaction.raw_imported_at).format('MMM D, YYYY HH:mm:ss')
                            : 'N/A'
                        }
                      />
                      <div className="pt-3.5">
                        <Lab className="mb-2 block">
                          Raw API response from {transaction.raw_exchange || transaction.exchange}
                        </Lab>
                        <JSONViewer data={transaction.raw_data} maxHeight="500px" />
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            )}

            <div className="mt-[var(--gap)] flex justify-end gap-3">
              {isEditing ? (
                <>
                  {hasChanges(values) && (
                    <Pill
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        resetForm();
                        setMutationError(null);
                        setMutationSuccess(false);
                      }}
                    >
                      <ArrowUturnLeftIcon className="h-4 w-4" />
                      Restore
                    </Pill>
                  )}
                  <Pill
                    type="submit"
                    variant={hasChanges(values) ? "solid" : "off"}
                    disabled={!hasChanges(values)}
                  >
                    Save changes
                  </Pill>
                </>
              ) : !isViewingClient ? (
                <Pill onClick={() => setIsEditing?.(true)}>
                  <PencilIcon className="h-4 w-4" />
                  Reclassify this transaction
                </Pill>
              ) : null}
            </div>

            <Foot right="Page 1 of 1" className="mt-[var(--gap)] px-0 pb-0">
              Every figure resolves to a source record you can export.
            </Foot>
          </Form>
        )}
      </Formik>
    </div>
  );
};
